/* Ingest — the tokens that let a webhook write into this salon's book.
 *
 * `tenant_ingest_keys` is the one table in the schema that is unreachable from
 * any browser session, admin or not: RLS with no policy and no grant, so
 * PostgREST does not even advertise it. Everything on this page therefore goes
 * through the `admin-api` function, which holds the service role.
 *
 * That is not caution for its own sake. A row here is a salon's identity to the
 * outside world — whoever holds the token can post appointments into their book
 * — and the digest is all that is stored, so the token cannot be recovered by
 * anybody, including us. That design comes from 01-tenancy.sql and this page
 * does not soften it: a token is displayed once, at the moment it is issued, and
 * then it is gone.
 *
 * The two sources are different ways in rather than different permissions:
 *
 *   dreamcloud   the existing Cloudflare Worker forwards the Mangomint webhooks
 *                it already receives, with the token in a header.
 *   mangomint    a salon whose Mangomint posts to us directly, with the token in
 *                the URL path — Mangomint sends no custom headers, so the path
 *                is the only place a secret can ride.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { nav } from '../nav.js';
import { store } from '../store.js';
import { ConfirmButton } from '../components/confirm-button.js';

var SOURCES = [
  { id: 'dreamcloud', name: 'DreamCloud forwarder',
    note: 'Header token. The worker forwards what Mangomint sends it.' },
  { id: 'mangomint', name: 'Mangomint direct',
    note: 'Path token. Mangomint posts straight to the ingest function.' }
];

var Ingest = {
  name: 'AdminIngest',
  components: { ConfirmButton: ConfirmButton },
  setup: function () {
    var keys = Vue.ref([]);
    var loaded = Vue.ref(false);
    var source = Vue.ref('dreamcloud');
    var label = Vue.ref('');
    // The one place a token ever exists in this app. Deliberately not stored
    // anywhere else and cleared the moment the page is left or a new one is
    // issued — there is nothing to come back to.
    var issued = Vue.ref(null);
    var copied = Vue.ref(false);

    async function load() {
      var id = store.selectedId.value;
      if (!id) return;
      loaded.value = false;
      var res = await api.call('ingest.list', { tenantId: id });
      if (res) keys.value = res.keys || [];
      loaded.value = true;
    }

    // Reloading the list must not take the freshly issued token off the screen
    // with it — issuing one reloads immediately, and that value exists nowhere
    // else. So `load` leaves it alone and only a change of salon clears it.
    Vue.watch(store.selectedId, function () {
      issued.value = null;
      load();
    }, { immediate: true });

    async function issue() {
      var id = store.selectedId.value;
      var res = await api.call('ingest.issue', {
        tenantId: id, source: source.value, label: label.value.trim() || null
      });
      if (!res) return;
      issued.value = res.token;
      copied.value = false;
      label.value = '';
      await load();
      store.loadTenants();
    }

    async function revoke(key) {
      var res = await api.call('ingest.revoke', { keyId: key.id });
      if (!res) return;
      api.toast('Revoked. A webhook presenting it is now refused rather than unrecognised.');
      load();
      store.loadTenants();
    }

    function copy() {
      if (!issued.value || !navigator.clipboard) return;
      navigator.clipboard.writeText(issued.value).then(function () {
        copied.value = true;
      });
    }

    var active = Vue.computed(function () {
      return keys.value.filter(function (k) { return !k.revoked_at; });
    });

    return {
      keys: keys,
      active: active,
      loaded: loaded,
      sources: SOURCES,
      source: source,
      label: label,
      issue: issue,
      issued: issued,
      copy: copy,
      copied: copied,
      revoke: revoke,
      t: store.selected,
      stamp: store.stamp,
      ago: store.ago,
      goPage: nav.goPage
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Ingest keys</h2>',
    '    <p class="page__lead">How this salon&rsquo;s booking data is attributed to it. The',
    '      token <strong>is</strong> the tenancy: the ingest function runs as the service role',
    '      and has no session to fall back on, so whoever holds one writes into this book.</p>',
    '  </div>',
    '',
    '  <div v-if="issued" class="card card--reveal">',
    '    <h3>Here is the token. This is the only time it is shown.</h3>',
    '    <p class="hint">Only its sha256 is stored, so it cannot be read back &mdash; by this',
    '      app, by the database, or by anybody with a backup of it. Lose it and the only fix is',
    '      to revoke the key and issue another.</p>',
    '    <p class="token mono">{{ issued }}</p>',
    '    <div class="card__row">',
    '      <button type="button" class="btn btn--primary btn--sm" @click="copy">',
    '        {{ copied ? "Copied" : "Copy" }}</button>',
    '      <button type="button" class="btn btn--quiet btn--sm" @click="issued = null">',
    '        I have saved it</button>',
    '    </div>',
    '    <p class="hint">For the DreamCloud forwarder:',
    '      <code>npx wrangler secret put TURNWIZARD_INGEST_TOKEN</code>.</p>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <h3>Issue a key</h3>',
    '    <div class="card__row">',
    '      <label class="field">',
    '        <span class="field__label">Source</span>',
    '        <select v-model="source" class="input">',
    '          <option v-for="s in sources" :key="s.id" :value="s.id">{{ s.name }}</option>',
    '        </select>',
    '      </label>',
    '      <label class="field field--grow">',
    '        <span class="field__label">Label</span>',
    '        <input v-model="label" class="input" type="text" maxlength="80"',
    '          placeholder="What this one is for" />',
    '      </label>',
    '      <button type="button" class="btn btn--primary" @click="issue">Issue</button>',
    '    </div>',
    '    <ul class="notes">',
    '      <li v-for="s in sources" :key="s.id"><strong>{{ s.name }}</strong> &mdash; {{ s.note }}</li>',
    '    </ul>',
    '    <p class="hint">Issuing a second key for the same source does not revoke the first.',
    '      Both work until one is revoked, which is what makes rolling a token a two-step with',
    '      no gap in the middle.</p>',
    '  </div>',
    '',
    '  <p v-if="!loaded" class="empty">Loading keys…</p>',
    '  <p v-else-if="!keys.length" class="empty">This salon has no ingest key, so nothing can',
    '    write into its book. If its board shows no appointments, this is why.</p>',
    '',
    '  <table v-else class="grid grid--plain">',
    '    <thead><tr><th>Source</th><th>Label</th><th>Issued</th><th>Last used</th>',
    '      <th>State</th><th></th></tr></thead>',
    '    <tbody>',
    '      <tr v-for="k in keys" :key="k.id" :class="{ \'is-muted\': k.revoked_at }">',
    '        <td class="mono">{{ k.source }}</td>',
    '        <td>{{ k.label || "—" }}</td>',
    '        <td :title="stamp(k.created_at)">{{ ago(k.created_at) }}</td>',
    '        <td :title="stamp(k.last_seen_at)">{{ ago(k.last_seen_at) }}</td>',
    '        <td>',
    '          <span class="pill" :class="k.revoked_at ? \'pill--bad\' : \'pill--ok\'">',
    '            {{ k.revoked_at ? "revoked" : "active" }}</span>',
    '        </td>',
    '        <td>',
    '          <confirm-button v-if="!k.revoked_at" label="Revoke" size="sm"',
    '            confirm="Revoke — webhooks using it start failing"',
    '            @confirmed="revoke(k)" />',
    '        </td>',
    '      </tr>',
    '    </tbody>',
    '  </table>',
    '',
    '  <div class="card">',
    '    <h3>What the book looks like now</h3>',
    '    <dl class="facts" v-if="t">',
    '      <div><dt>Appointments mirrored</dt><dd>{{ t.appointment_count }}</dd></div>',
    '      <div><dt>Last write from a webhook</dt>',
    '        <dd :title="stamp(t.last_synced_at)">{{ ago(t.last_synced_at) }}</dd></div>',
    '      <div><dt>Last token presented</dt>',
    '        <dd :title="stamp(t.ingest_last_seen_at)">{{ ago(t.ingest_last_seen_at) }}</dd></div>',
    '    </dl>',
    '    <p class="hint">A token last used long before the last sync means something else is',
    '      writing this book. A token used recently with no sync means the webhook is arriving',
    '      and being rejected further in &mdash; the function&rsquo;s logs, not this page.</p>',
    '  </div>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'ingest',
  name: 'Ingest keys',
  group: 'Connections',
  page: Ingest
});

export { Ingest };
